if (!window.Romanics.loadedScript.has('purcomfy-motion.js')) {
  window.Romanics.loadedScript.add('purcomfy-motion.js');

  (() => {
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const state = {
      reducedMotion: reducedMotionQuery.matches,
      lastCartCount: 0,
      cartCountFrame: null,
      cartDrawerOffsetFrame: null,
      pointerFrame: null,
      pointerEvent: null,
      storyRevealFrame: null,
      storyRevealEventsBound: false,
      storyRevealRoots: new Set()
    };
    const cartCountBadgeSelector = '.purcomfy-header__cart-badge';
    const storyRevealSelector = '[data-purcomfy-routine-story]';
    const storyRevealStartRatio = 0.82;
    const storyRevealScrollRatio = 0.7;

    const getElements = (target) => {
      if (!target) return [];
      if (typeof target === 'string') return Array.from(document.querySelectorAll(target));
      if (target instanceof Element) return [target];
      if (target instanceof NodeList || Array.isArray(target)) return Array.from(target).filter(Boolean);

      return [];
    };

    const resetPointerVars = (elements) => {
      getElements(elements || '.purcomfy-motion-pointer').forEach((el) => {
        el.style.setProperty('--purcomfy-pointer-x', '0');
        el.style.setProperty('--purcomfy-pointer-y', '0');
        el.style.setProperty('--purcomfy-scroll-x', '0');
        el.style.setProperty('--purcomfy-scroll-y', '0');
      });
    };

    const onReady = (callback) => {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback, { once: true });
        return;
      }

      callback();
    };

    const prefersReducedMotion = () => state.reducedMotion;

    const parseCartCount = (value) => {
      const count = parseInt(String(value || '').replace(/[^\d]/g, ''), 10);
      return Number.isFinite(count) ? count : 0;
    };

    const getCartBadgeCount = (badge) => {
      if (!badge) return 0;
      return parseCartCount(badge.textContent);
    };

    const isVisible = (el) => {
      if (!el || el.classList.contains('hidden')) return false;
      const styles = window.getComputedStyle(el);
      return styles.display !== 'none' && styles.visibility !== 'hidden' && el.getClientRects().length > 0;
    };

    const getCartBadges = () => getElements(cartCountBadgeSelector);

    const getVisibleCartBadges = () => getCartBadges().filter((badge) => {
      return isVisible(badge) && getCartBadgeCount(badge) > 0;
    });

    const getCurrentCartCount = () => {
      const counts = getCartBadges().map(getCartBadgeCount);
      return counts.length ? Math.max(...counts) : 0;
    };

    const triggerClass = (target, className, timeout = 800) => {
      if (state.reducedMotion || !className) return;

      getElements(target).forEach((el) => {
        let fallbackTimer;
        const cleanup = () => {
          window.clearTimeout(fallbackTimer);
          el.classList.remove(className);
          el.removeEventListener('animationend', cleanup);
        };

        el.classList.remove(className);
        void el.offsetWidth;
        el.classList.add(className);
        el.addEventListener('animationend', cleanup, { once: true });
        fallbackTimer = window.setTimeout(cleanup, timeout);
      });
    };

    const getNumber = (value, fallback) => {
      const number = Number.parseFloat(value);
      return Number.isFinite(number) ? number : fallback;
    };

    const clamp = (value, min = 0, max = 1) => {
      return Math.min(max, Math.max(min, value));
    };

    const syncCartDrawerOffset = () => {
      state.cartDrawerOffsetFrame = null;

      const header = document.querySelector('.purcomfy-header-section') || document.querySelector('.section-header');
      const rect = header?.getBoundingClientRect();
      const drawerTop = rect ? Math.max(0, Math.round(rect.bottom)) : 0;

      document.documentElement.style.setProperty('--purcomfy-cart-drawer-top', `${drawerTop + 16}px`);
      document.documentElement.style.setProperty('--purcomfy-cart-drawer-height', `calc(100dvh - ${drawerTop + 16}px)`);
    };

    const queueCartDrawerOffset = () => {
      if (state.cartDrawerOffsetFrame) return;

      state.cartDrawerOffsetFrame = window.requestAnimationFrame(syncCartDrawerOffset);
    };

    const bindCartDrawerOffset = () => {
      queueCartDrawerOffset();

      window.addEventListener('scroll', queueCartDrawerOffset, { passive: true });
      window.addEventListener('resize', queueCartDrawerOffset);
      window.visualViewport?.addEventListener('resize', queueCartDrawerOffset);
      document.addEventListener('romanics:sticky-header-moved', queueCartDrawerOffset);
    };

    const getCascadeTextNodes = (root) => {
      const textNodes = [];
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
          if (node.parentElement && node.parentElement.closest('.purcomfy-cascade-letter')) {
            return NodeFilter.FILTER_REJECT;
          }

          return NodeFilter.FILTER_ACCEPT;
        }
      });

      while (walker.nextNode()) {
        textNodes.push(walker.currentNode);
      }

      return textNodes;
    };

    const createCascadeLetter = (doc, character, delay) => {
      const letter = doc.createElement('span');
      const inner = doc.createElement('span');

      letter.className = 'purcomfy-cascade-letter';
      letter.setAttribute('aria-hidden', 'true');
      letter.style.setProperty('--purcomfy-letter-delay', `${delay}ms`);

      inner.className = 'purcomfy-cascade-letter-inner';
      inner.textContent = character;

      letter.appendChild(inner);

      return letter;
    };

    const splitCascadeTextNode = (textNode, timing) => {
      const doc = textNode.ownerDocument;
      const fragment = doc.createDocumentFragment();
      const chunks = textNode.nodeValue.split(/(\s+)/);

      chunks.forEach((chunk) => {
        if (!chunk) return;

        if (/^\s+$/.test(chunk)) {
          fragment.appendChild(doc.createTextNode(chunk));
          return;
        }

        const word = doc.createElement('span');
        word.className = 'purcomfy-cascade-word';

        Array.from(chunk).forEach((character) => {
          const delay = timing.baseDelay + timing.index * timing.delayStep;
          word.appendChild(createCascadeLetter(doc, character, delay));
          timing.index += 1;
        });

        fragment.appendChild(word);
      });

      textNode.parentNode.replaceChild(fragment, textNode);
    };

    const restoreCascadeHeading = (heading, originalHtml) => {
      if (!heading || !heading.isConnected || heading.dataset.purcomfyCascadeReady !== 'true') return;

      heading.innerHTML = originalHtml;
      heading.removeAttribute('aria-label');
      heading.dataset.purcomfyCascadeReady = 'settled';
    };

    const queueCascadeHeadingRestore = (heading, originalHtml) => {
      const letters = heading.querySelectorAll('.purcomfy-cascade-letter-inner');
      const lastLetter = letters[letters.length - 1];

      if (!lastLetter) return;

      let fallbackTimer;
      const restore = (event) => {
        if (event && event.propertyName !== 'transform') return;

        window.clearTimeout(fallbackTimer);
        lastLetter.removeEventListener('transitionend', restore);
        window.setTimeout(() => restoreCascadeHeading(heading, originalHtml), 40);
      };

      lastLetter.addEventListener('transitionend', restore);
      fallbackTimer = window.setTimeout(() => {
        if (!heading.closest('.purcomfy-cascade')?.classList.contains('is-visible')) return;
        restoreCascadeHeading(heading, originalHtml);
      }, 5000);
    };

    const splitCascadeHeading = (heading) => {
      if (!heading || state.reducedMotion || heading.dataset.purcomfyCascadeReady) return;

      const label = heading.textContent.replace(/\s+/g, ' ').trim();
      if (!label) return;

      const originalHtml = heading.innerHTML;

      heading.setAttribute('aria-label', label);

      const timing = {
        baseDelay: getNumber(heading.dataset.purcomfyLetterBaseDelay, 100),
        delayStep: getNumber(heading.dataset.purcomfyLetterDelayStep, 28),
        index: 0
      };

      getCascadeTextNodes(heading).forEach((textNode) => {
        splitCascadeTextNode(textNode, timing);
      });

      heading.dataset.purcomfyCascadeReady = 'true';
      queueCascadeHeadingRestore(heading, originalHtml);
    };

    const getCascadeHeadings = (scope = document) => {
      const headings = [];

      if (scope.matches && scope.matches('[data-purcomfy-cascade-heading]')) {
        headings.push(scope);
      }

      if (scope.querySelectorAll) {
        headings.push(...scope.querySelectorAll('[data-purcomfy-cascade-heading]'));
      }

      return headings;
    };

    const bindCascadeHeadings = (scope = document) => {
      if (state.reducedMotion) return;

      getCascadeHeadings(scope).forEach(splitCascadeHeading);
    };

    const getStoryRevealTextNodes = (root) => {
      const textNodes = [];
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
          if (node.parentElement && node.parentElement.closest('.purcomfy-routine-story-word')) {
            return NodeFilter.FILTER_REJECT;
          }

          return NodeFilter.FILTER_ACCEPT;
        }
      });

      while (walker.nextNode()) {
        textNodes.push(walker.currentNode);
      }

      return textNodes;
    };

    const splitStoryRevealTextNode = (textNode) => {
      const doc = textNode.ownerDocument;
      const fragment = doc.createDocumentFragment();
      const chunks = textNode.nodeValue.split(/(\s+)/);

      chunks.forEach((chunk) => {
        if (!chunk) return;

        if (/^\s+$/.test(chunk)) {
          fragment.appendChild(doc.createTextNode(chunk));
          return;
        }

        const word = doc.createElement('span');
        word.className = 'purcomfy-routine-story-word';
        word.textContent = chunk;
        fragment.appendChild(word);
      });

      textNode.parentNode.replaceChild(fragment, textNode);
    };

    const splitStoryReveal = (root) => {
      if (!root || root.dataset.purcomfyRoutineStoryReady === 'true') return;

      getStoryRevealTextNodes(root).forEach(splitStoryRevealTextNode);
      root.dataset.purcomfyRoutineStoryReady = 'true';
    };

    const getStoryRevealRoots = (scope = document) => {
      const roots = [];

      if (scope.matches && scope.matches(storyRevealSelector)) {
        roots.push(scope);
      }

      if (scope.querySelectorAll) {
        roots.push(...scope.querySelectorAll(storyRevealSelector));
      }

      return roots;
    };

    const syncStoryRevealRoot = (root) => {
      if (!root || !root.isConnected) {
        state.storyRevealRoots.delete(root);
        return;
      }

      const words = Array.from(root.querySelectorAll('.purcomfy-routine-story-word'));
      if (!words.length) return;

      if (state.reducedMotion) {
        words.forEach((word) => {
          word.style.setProperty('--purcomfy-routine-word-opacity', '1');
        });
        return;
      }

      const rect = root.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const start = viewportHeight * storyRevealStartRatio;
      const progress = clamp((start - rect.top) / (viewportHeight * storyRevealScrollRatio));
      const baseOpacity = 0.18;
      const reveal = progress * words.length;

      words.forEach((word, index) => {
        const wordProgress = clamp(reveal - index);
        const opacity = baseOpacity + (1 - baseOpacity) * wordProgress;

        word.style.setProperty('--purcomfy-routine-word-opacity', opacity.toFixed(3));
      });
    };

    const syncStoryReveals = () => {
      state.storyRevealFrame = null;
      state.storyRevealRoots.forEach(syncStoryRevealRoot);
    };

    const queueStoryReveals = () => {
      if (state.storyRevealFrame) return;

      state.storyRevealFrame = window.requestAnimationFrame(syncStoryReveals);
    };

    const bindStoryRevealEvents = () => {
      if (state.storyRevealEventsBound) return;

      window.addEventListener('scroll', queueStoryReveals, { passive: true });
      window.addEventListener('resize', queueStoryReveals);
      state.storyRevealEventsBound = true;
    };

    const bindStoryReveals = (scope = document) => {
      const roots = getStoryRevealRoots(scope);
      if (!roots.length) return;

      roots.forEach((root) => {
        splitStoryReveal(root);
        state.storyRevealRoots.add(root);
      });

      bindStoryRevealEvents();
      queueStoryReveals();
    };

    const syncCartCount = () => {
      state.cartCountFrame = null;

      const currentCartCount = getCurrentCartCount();
      if (currentCartCount > state.lastCartCount) {
        triggerClass(getVisibleCartBadges(), 'is-cart-count-bouncing', 700);
      }

      state.lastCartCount = currentCartCount;
    };

    const queueCartCountSync = () => {
      if (state.cartCountFrame) window.cancelAnimationFrame(state.cartCountFrame);
      state.cartCountFrame = window.requestAnimationFrame(() => {
        window.setTimeout(syncCartCount, 0);
      });
    };

    const bindCartCountBounce = () => {
      state.lastCartCount = getCurrentCartCount();
      document.addEventListener('romanics:cart:items-changed', queueCartCountSync);
    };

    const bindPointerVars = (selector = '.purcomfy-motion-pointer') => {
      const updatePointerVars = () => {
        const event = state.pointerEvent;
        state.pointerFrame = null;

        if (state.reducedMotion) {
          resetPointerVars(selector);
          return;
        }

        const pageScroll = window.scrollY || document.documentElement.scrollTop || 0;

        getElements(selector).forEach((el) => {
          const rect = el.getBoundingClientRect();
          if (!rect.width || !rect.height) return;

          const x = event ? ((event.clientX - rect.left) / rect.width - 0.5).toFixed(3) : '0';
          const y = event ? ((event.clientY - rect.top) / rect.height - 0.5).toFixed(3) : '0';
          const scrollSeed = pageScroll + rect.top + rect.left;
          const scrollX = Math.sin(scrollSeed * 0.004).toFixed(3);
          const scrollY = Math.cos(scrollSeed * 0.003).toFixed(3);

          el.style.setProperty('--purcomfy-pointer-x', x);
          el.style.setProperty('--purcomfy-pointer-y', y);
          el.style.setProperty('--purcomfy-scroll-x', scrollX);
          el.style.setProperty('--purcomfy-scroll-y', scrollY);
        });
      };

      const queuePointerVars = () => {
        if (state.pointerFrame) return;
        state.pointerFrame = window.requestAnimationFrame(updatePointerVars);
      };

      const onPointerMove = (event) => {
        state.pointerEvent = event;
        queuePointerVars();
      };

      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('scroll', queuePointerVars, { passive: true });
      window.addEventListener('resize', queuePointerVars);
      queuePointerVars();

      return () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('scroll', queuePointerVars);
        window.removeEventListener('resize', queuePointerVars);
        if (state.pointerFrame) window.cancelAnimationFrame(state.pointerFrame);
        state.pointerFrame = null;
        state.pointerEvent = null;
        resetPointerVars(selector);
      };
    };

    const updateReducedMotion = (event) => {
      state.reducedMotion = event.matches;
      if (state.reducedMotion) resetPointerVars();
      else bindCascadeHeadings();
      queueStoryReveals();
    };

    if (reducedMotionQuery.addEventListener) {
      reducedMotionQuery.addEventListener('change', updateReducedMotion);
    } else {
      reducedMotionQuery.addListener(updateReducedMotion);
    }

    window.PurcomfyMotion = Object.assign(window.PurcomfyMotion || {}, {
      bindCartCountBounce,
      bindCartDrawerOffset,
      bindCascadeHeadings,
      bindStoryReveals,
      bindPointerVars,
      prefersReducedMotion,
      resetPointerVars,
      splitCascadeHeading,
      triggerClass
    });

    if (window.PointerEvent) {
      bindPointerVars('.purcomfy-motion-stars');
    }

    onReady(() => {
      bindCascadeHeadings();
      bindStoryReveals();
    });

    document.addEventListener('shopify:section:load', (event) => {
      queueCartDrawerOffset();
      bindCascadeHeadings(event.target);
      bindStoryReveals(event.target);
    });

    bindCartDrawerOffset();
    bindCartCountBounce();
  })();
}
